import {FC, useEffect, useRef, useState} from "react"
import {Trans} from "react-i18next"
import {useNavigate, useSearchParams} from "react-router-dom"
import {ReactSVG} from "react-svg"

import {useBreadcrumbs, useCase} from "@frontend/app-container/context"
import {FeedbackButton} from "@frontend/components"
import {linkStyles} from "@frontend/components/ui"
import oven from "@frontend/design/icons/oven.svg"
import refrigerator from "@frontend/design/icons/refrigerator.svg"
import washingMachine from "@frontend/design/icons/washing-machine.svg"
import {
  getExternalSessionId,
  getHostBridge,
  getMergedInitPageContext,
} from "@frontend/hostContract"
import {useTranslation} from "@frontend/i18n"
import {InitialCaseState} from "@frontend/routes/util/initialCaseState"
import {makePath} from "@frontend/routing"
import {useSession} from "@frontend/session"
import {activeTheme} from "@frontend/themes/activeTheme"
import {
  emitHostCaseCreated,
  usesJourneyHostEvents,
} from "@frontend/utils/hostJourneyEvents"
import {trackEvent} from "@frontend/utils/trackEvent"
import {useCreateCaseMutation} from "@frontend/utils/trpc"
import {useBooleanState} from "@frontend/utils/useBooleanState"
import {useEffectOnMount} from "@frontend/utils/useEffectOnMount"
import {ProductTitleMark} from "@frontend/widget/ProductTitleMark"
import {widgetChrome} from "@frontend/widget/brandChrome"
import {css, cx} from "@styled-system/css"
import {hstack, iconsColor, vstack} from "@styled-system/patterns"

import {BaseLayout} from "./cases/[caseId]/components/base-layout"
import {ConsentModal} from "./cases/[caseId]/components/consent-modal"
import {ConversationInputField} from "./cases/[caseId]/components/conversation-input-field"

const MAX_WIDTH = 840

/** Bodewell renders a text wordmark instead of a graphic logo mark on the landing hero. */
const hasLogoMarkHero =
  activeTheme.capabilities.landingHeroLayout === "logoMark"
const isGreetingStartAligned =
  activeTheme.capabilities.chatGreetingAlign === "start"
const {accentGlyph: homeVector} = activeTheme.assets

export const LandingPage: FC = () => {
  useBreadcrumbs([])
  useCase()

  const {
    state: showConsentModal,
    setTrue: onShowConsentModal,
    setFalse: onHideConsentModal,
  } = useBooleanState(false)

  const inputRef = useRef<HTMLTextAreaElement>(null)
  const pendingMessageRef = useRef<string | null>(null)
  const t = useTranslation()

  const {userRcId} = useSession()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [text, setText] = useState("")
  const [preWrittenMessage, setPreWrittenMessage] = useState("")
  const trimmedText = preWrittenMessage ? preWrittenMessage.trim() : text.trim()

  const createCaseVariables = () => ({
    userRcId,
    externalSessionId: getExternalSessionId(),
    pageContext: getMergedInitPageContext(),
    surface: "chat" as const,
  })

  const onCreateCase = (): void => {
    if (!trimmedText) return
    pendingMessageRef.current = trimmedText
    createCaseMutation.mutate(createCaseVariables())
  }

  const createCase = (text: string) => (): void => {
    setPreWrittenMessage(text)
    pendingMessageRef.current = text.trim()
    createCaseMutation.mutate(createCaseVariables())
  }

  const createCaseMutation = useCreateCaseMutation(({caseId}): void => {
    const messageText = pendingMessageRef.current || trimmedText
    pendingMessageRef.current = null
    const initialCaseState: InitialCaseState = {
      text: messageText,
    }

    if (usesJourneyHostEvents()) {
      emitHostCaseCreated(caseId)
      trackEvent("chat_started", caseId)
    } else {
      trackEvent("chat_started", caseId)
      getHostBridge()?.onCaseCreated?.(caseId)
    }

    navigate(makePath({name: "cases", caseId, ...initialCaseState}))
  })

  const preWrittenMessages = [
    {
      message: t("home.preWrittenMessages.washingMachine"),
      icon: washingMachine,
    },
    {
      message: t("home.preWrittenMessages.refrigerator"),
      icon: refrigerator,
    },
    {
      message: t("home.preWrittenMessages.stoveBurner"),
      icon: oven,
    },
  ]

  const [autoSubmit, setAutoSubmit] = useState(false)

  useEffectOnMount(() => {
    const hostMsg = searchParams.get("hostMsg")
    if (hostMsg) {
      setText(hostMsg)
      setAutoSubmit(true)
      return
    }
    inputRef.current?.focus()
  })

  useEffect(() => {
    if (autoSubmit && trimmedText) {
      setAutoSubmit(false)
      pendingMessageRef.current = trimmedText
      createCaseMutation.mutate(createCaseVariables())
    }
  }, [autoSubmit, trimmedText]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <BaseLayout>
        <div
          className={vstack({
            minHeight: "100%",
            width: "100%",
            gap: 32,
            paddingY: 24,
            paddingX: {
              base: 16,
              desktop: 0,
            },
            alignItems: {
              base: "stretch",
              desktop: "center",
            },
            backgroundColor: "background.primary",
            desktop: {
              // Bodewell packs title + card + powered-by; keep consent in view
              // instead of vertically distributing into overflow.
              justifyContent: hasLogoMarkHero ? "space-between" : "flex-start",
              marginX: 8,
            },
            desktopOnly: {
              marginX: 0,
              paddingX: 48,
            },
          })}
        >
          {hasLogoMarkHero ? <div /> : null}
          <div
            className={vstack({
              gap: {
                base: 16,
                desktop: hasLogoMarkHero ? 32 : 16,
              },
              alignItems: {
                base: "stretch",
                desktop: isGreetingStartAligned ? "stretch" : "center",
              },
              width: isGreetingStartAligned ? "100%" : undefined,
              maxWidth: isGreetingStartAligned ? MAX_WIDTH : undefined,
              // Logo themes keep a large spacer above the footer; Bodewell's
              // taller hero (title + powered-by) would push consent below the fold.
              marginBottom: {
                desktop: hasLogoMarkHero ? 160 : 0,
              },
            })}
          >
            {!hasLogoMarkHero && (
              <h1
                className={css({
                  margin: 0,
                  desktop: {
                    textAlign: isGreetingStartAligned ? "left" : "center",
                  },
                })}
              >
                <ProductTitleMark
                  title={widgetChrome.greetingTitle}
                  starSize={28}
                  className={css({
                    fontSize: {
                      base: 28,
                      desktop: 40,
                    },
                    fontWeight: 700,
                    lineHeight: 1.2,
                    letterSpacing: "0.02em",
                  })}
                />
              </h1>
            )}
            <p
              className={css({
                // Supporting heading under the greeting (RvR h5 → h3), not body.
                textStyle: hasLogoMarkHero
                  ? "header.m"
                  : {
                      base: "h5",
                      desktop: "h3",
                    },
                textAlign: isGreetingStartAligned ? "left" : undefined,
                desktop: {
                  textAlign: isGreetingStartAligned ? "left" : "center",
                },
                // Clear the decorative star that sits on the card’s top-left.
                marginBottom: isGreetingStartAligned
                  ? {base: 16, desktop: 32}
                  : undefined,
                color: "text.primary",
              })}
            >
              {widgetChrome.homeWelcome ?? t("home.welcome")}
            </p>
            <div
              className={vstack({
                width: "100%",
                maxWidth: MAX_WIDTH,
                alignItems: "stretch",
                padding: 32,
                borderRadius: 16,
                position: "relative",
                gap: 24,
                backgroundColor: "background.brand.primary",
              })}
            >
              <div
                className={css({
                  position: "absolute",
                  top: "-46px",
                  left: "-46px",
                  display: "none",
                  desktop: {
                    display: "block",
                  },
                })}
              >
                <ReactSVG
                  src={homeVector}
                  className={iconsColor({color: "foreground.brand.accent"})}
                />
              </div>
              <p
                className={css({
                  textStyle: "bodyXLMedium",
                  color: "text.primary",
                })}
              >
                {t("home.prompt")}
              </p>
              <ConversationInputField
                onChange={setText}
                value={text}
                onSend={onCreateCase}
                onAbort={() => ({})}
                disabled={createCaseMutation.isPending || !trimmedText}
                inputRef={inputRef}
                isFirstMessage
              />
              <div
                className={css({
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                  justifyContent: "flex-start",
                  gap: 8,

                  desktop: {
                    flexDirection: "row",
                    gap: 16,
                  },
                })}
              >
                <p
                  className={css({
                    flexGrow: 0,
                    flexShrink: 0,
                    textStyle: "bodyRegular",
                    color: "text.tertiary",

                    desktop: {
                      alignSelf: "center",
                    },
                  })}
                >
                  {t("home.preWrittenMessages.title")}
                </p>
                {preWrittenMessages.map(({message, icon}) => (
                  <DefaultPrompt
                    key={message}
                    icon={icon}
                    message={message}
                    onClick={createCase(message)}
                  />
                ))}
              </div>
            </div>
            {/* <SupportedExplanation /> */}
          </div>
          <div
            className={css({
              display: "flex",
              flexDirection: "column",
              width: "100%",
              maxWidth: MAX_WIDTH,
              gap: 32,
              paddingY: 16,
              borderTopWidth: 1,
              borderColor: "border.secondary.200",
              // Pin consent to the bottom of the viewport when space allows,
              // without the old 160px spacer that pushed it below the fold.
              marginTop: "auto",

              desktop: {
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
              },
            })}
          >
            <p
              className={css({
                textStyle: "captionRegular",
                color: "text.quaternary",
              })}
            >
              <Trans
                t={t}
                i18nKey="home.beta"
                values={{productTitle: widgetChrome.productTitle}}
              >
                <span
                  onClick={onShowConsentModal}
                  className={cx(
                    linkStyles({variant: "footer"}),
                    css({cursor: "pointer"}),
                  )}
                />
              </Trans>
            </p>
            <FeedbackButton css={css.raw({flexGrow: 0, flexShrink: 0})} />
          </div>
        </div>
      </BaseLayout>
      <ConsentModal show={showConsentModal} onClose={onHideConsentModal} />
    </>
  )
}

const DefaultPrompt: FC<{
  message: string
  icon: string
  onClick: () => void
}> = ({message, icon, onClick}) => (
  <button
    onClick={onClick}
    className={hstack({
      gap: 8,
      borderRadius: 8,
      paddingY: 8,
      paddingX: 12,
      backgroundColor: "background.primary",
      borderWidth: 1,
      borderColor: "border.secondary.200",
      justifyContent: "flex-start",
      transition: "background-color 0.1s ease-in, box-shadow 0.1s ease-in",
      "&:hover": {
        backgroundColor: "background.primary",
        boxShadow:
          "0px 9px 20px rgba(28, 37, 72, 0.14), 0px 3px 3px rgba(28, 37, 72, 0.06)",
      },
    })}
  >
    <ReactSVG
      src={icon}
      className={iconsColor({color: "foreground.brand.primary"})}
    />
    <p
      className={css({
        color: "text.secondary",
        textStyle: "captionRegular",
        textAlign: "left",
      })}
    >
      {message}
    </p>
  </button>
)
